import { fromJS } from 'immutable';
import { makeImmutable } from 'utils/immutableUtils';
import { createInitialFormBuilderState } from 'utils/editorDefaultStates';
import { addStep, removeStep } from './ops.step';
import {
  addField,
  bulkToggleDropdownOptions,
  deleteField,
  moveField,
  reorderDropdown,
  updateFormValidity,
  collapseAllFields,
  expandAllFields,
  setInferredState,
  updateFieldOverrideState,
} from './ops.field';
import { changeConsentStyle, setForm } from './ops.form';
import { makeErrorsVisible, makeDirty, trimEmptyLeaves } from './ops.ui';
import {
  toggleBranching,
  toggleBranchMembership,
  updateFieldSpecificConditions,
  removeAllUserBranchMembership,
  toggleThankYouRoute,
  setThankYouRoute,
  refreshThankYouRoute,
} from './ops.conditionality';
import {
  ADD_FIELD,
  ADD_STEP,
  BULK_TOGGLE_DROPDOWN_OPTIONS,
  CHANGE_CONSENT_STYLE,
  CLEAR_FORM,
  COLLAPSE_ALL_FIELDS,
  DELETE_FIELD,
  EXPAND_ALL_FIELDS,
  LOAD_IN_PROGRESS,
  RECEIVE_SF_FIELD_DATA,
  REFRESH_THANK_YOU_ROUTING,
  REINITIALIZE_FIELD_VALIDITY,
  REMOVE_ALL_USER_BRANCH_MEMBERSHIP,
  REMOVE_STEP,
  REORDER_DROPDOWN,
  REORDER_FIELD,
  SAVE_IN_PROGRESS,
  SET_AVAILABLE_CREDENTIAL_TYPES,
  SET_AVAILABLE_PROGRAMS,
  SET_AVAILABLE_PROGRAMS_OF_STUDY,
  SET_ACTIVE_FIELD,
  SET_DIRTY,
  SET_FIELD_INFERRED_STATE,
  SET_FORM,
  SET_SUPPORTS_DEGREE_OFFERING,
  SET_THANK_YOU_ROUTING,
  SHOW_FORM_ERRORS,
  TOGGLE_BRANCH_MEMBERSHIP,
  TOGGLE_BRANCHING,
  TOGGLE_FIELD_COLLAPSED_STATE,
  TOGGLE_THANK_YOU_ROUTING,
  UPDATE_DEFAULT_DEGREE_OFFERING,
  UPDATE_PROGRAM_OF_STUDY,
  UPDATE_FIELD_CONDITIONALITY,
  UPDATE_FIELD_OVERRIDE_STATE,
  UPDATE_FORM_VALIDITY,
  UPDATE_FORM_VALUE,
  UPDATE_UI_STATE,
} from '../constants';
import { CREDENTIAL_TYPES } from '../../../utils/constants';

const initialState = (credentialType) => fromJS(createInitialFormBuilderState(credentialType));

function formBuilderReducer(state = initialState(CREDENTIAL_TYPES.DEGREE), action) {
  switch (action.type) {
    case ADD_FIELD:
      return addField(state, action.id, action.index);
    case ADD_STEP:
      return addStep(state, action.id);
    case BULK_TOGGLE_DROPDOWN_OPTIONS:
      return bulkToggleDropdownOptions(state, action.id, action.dropdownOptions, action.value);
    case CHANGE_CONSENT_STYLE:
      return changeConsentStyle(state, action.consentStyle);
    case CLEAR_FORM:
      return initialState(action.credentialType);
    case COLLAPSE_ALL_FIELDS:
      return collapseAllFields(state);
    case DELETE_FIELD:
      return deleteField(state, action.keyPath);
    case EXPAND_ALL_FIELDS:
      return expandAllFields(state);
    case LOAD_IN_PROGRESS:
      return state.setIn(['meta', 'loadInProgress'], action.inProgress);
    case RECEIVE_SF_FIELD_DATA:
      return state.setIn(['meta', 'sfFieldData'], makeImmutable(action.sfFieldData))
        .setIn(['meta', 'fieldNameDropdownOpts'], makeImmutable(action.fieldNameDropdownOpts));
    case REINITIALIZE_FIELD_VALIDITY: {
      const newState = state.setIn(['meta', 'invalidFormData', ...action.keyPath], action.newValidity);
      if (!action.newValidity.size) {
        return trimEmptyLeaves(newState, ['meta', 'invalidFormData', ...action.keyPath]);
      }
      return newState;
    }
    case REMOVE_ALL_USER_BRANCH_MEMBERSHIP:
      return removeAllUserBranchMembership(state, action.fieldId);
    case REMOVE_STEP:
      return removeStep(state, action.id);
    case REORDER_DROPDOWN:
      return reorderDropdown(state, action);
    case REORDER_FIELD:
      return moveField(state, action.source, action.target);
    case SAVE_IN_PROGRESS:
      return state.setIn(['meta', 'saveInProgress'], action.inProgress);
    case SET_ACTIVE_FIELD:
      return state.setIn(['meta', 'uiState', 'activeFieldId'], makeImmutable(action.fieldId));
    case SET_AVAILABLE_CREDENTIAL_TYPES:
      return state.setIn(['meta', 'availableCredentialTypes'], makeImmutable(action.availableCredentialTypes));
    case SET_AVAILABLE_PROGRAMS:
      return state.setIn(['meta', 'availablePrograms'], action.programs);
    case SET_AVAILABLE_PROGRAMS_OF_STUDY:
      return state.setIn(['meta', 'availableProgramsOfStudy'], action.programsOfStudy);
    case SET_DIRTY:
      return state.setIn(['meta', 'isDirty'], action.isDirty);
    case SET_FIELD_INFERRED_STATE:
      return setInferredState(state, action.fieldName, action.value, action.fieldId);
    case SET_FORM:
      return setForm(state, action.form, action.shouldRebuildCollapsedState);
    case SET_SUPPORTS_DEGREE_OFFERING:
      return state.setIn(['meta', 'supportsDegreeOffering'], action.hasDegreeOffering);
    case SHOW_FORM_ERRORS:
      return makeErrorsVisible(state, ['meta', 'invalidFormData']);
    case TOGGLE_BRANCHING:
      return toggleBranching(state, action.fieldId);
    case TOGGLE_THANK_YOU_ROUTING:
      return toggleThankYouRoute(state, action.fieldId);
    case SET_THANK_YOU_ROUTING:
      return setThankYouRoute(state, action.fieldId, action.answer, action.url);
    case REFRESH_THANK_YOU_ROUTING:
      return refreshThankYouRoute(state, action.fieldId);
    case TOGGLE_BRANCH_MEMBERSHIP:
      return toggleBranchMembership(state, action.fieldId, action.answerValue, action.branchMemberId);
    case TOGGLE_FIELD_COLLAPSED_STATE:
      return state.setIn(['meta', 'uiState', 'fieldOrderCollapsedState', action.index], !state.getIn(['meta', 'uiState', 'fieldOrderCollapsedState', action.index]));
    case UPDATE_DEFAULT_DEGREE_OFFERING:
      return state.mergeIn(['form'], { ...action.data });
    case UPDATE_PROGRAM_OF_STUDY:
      return state.setIn(['form', 'programOfStudy'], { ...action.data });
    case UPDATE_FIELD_CONDITIONALITY:
      return updateFieldSpecificConditions(state, action.keyPath, action.newFieldName, action.previousFieldName);
    case UPDATE_FIELD_OVERRIDE_STATE:
      return updateFieldOverrideState(state, action.fieldId, action.fieldType, action.consentStyle);
    case UPDATE_FORM_VALIDITY:
      return updateFormValidity(state, action.keyPath, action.isValid, action.errorText, action.replaceError);
    case UPDATE_FORM_VALUE:
      if (!action.shouldMakeDirty) {
        return state.setIn(['form', ...action.keyPath], makeImmutable(action.value));
      }
      return makeDirty(state.setIn(['form', ...action.keyPath], makeImmutable(action.value)));
    case UPDATE_UI_STATE:
      return state.mergeIn(['meta', 'uiState', ...action.keyPath], makeImmutable(action.value));
    default:
      return state;
  }
}

export default formBuilderReducer;
